import { StrategyPhase, WeeklyStrategy } from '../ProfitScenario/types';

export const generateFullPlanData = (): StrategyPhase[] => {
    const baseWeekStrategy: Omit<WeeklyStrategy, 'weekNumber'> = {
        basePrice: 39.99,
        dailyUnits: 20,
        enablePrime: false,
        primeDiscount: 0,
        enableCoupon: false,
        couponDiscount: 0,
        enablePromotion: false,
        promotionDiscount: 0,
        promotionDailyUnits: 0,
        enableLightningDeal: false,
        lightningDealDiscount: 0,
        enableAds: true,
        cpc: 1.5,
        cvr: 5,
        adSalesRatio: 30
    };

    // 阶段1: 新品推广期 (W1-W4)
    const phase1Tasks = [
        // W1 任务
        [
            { id: 'p1w1t1', content: '开启自动广告，设置日预算 $50-100', completed: false, priority: 'high' as const, startDate: '2025-12-02', endDate: '2025-12-04' },
            { id: 'p1w1t2', content: '联系站外推广资源（Deal 站/红人）', completed: false, priority: 'high' as const, startDate: '2025-12-03', endDate: '2025-12-06' },
            { id: 'p1w1t3', content: '准备促销素材（Coupon/Promo 图片）', completed: false, priority: 'medium' as const, startDate: '2025-12-04', endDate: '2025-12-07' },
            { id: 'p1w1t4', content: '监控库存水平，确保不断货', completed: false, priority: 'medium' as const, startDate: '2025-12-05', endDate: '2025-12-08' }
        ],
        // W2 任务
        [
            { id: 'p1w2t1', content: '查看广告报告，调整无效关键词', completed: false, priority: 'high' as const, startDate: '2025-12-09', endDate: '2025-12-11' },
            { id: 'p1w2t2', content: '优化主图和A+页面的点击率', completed: false, priority: 'high' as const, startDate: '2025-12-10', endDate: '2025-12-13' },
            { id: 'p1w2t3', content: '联系更多站外资源，扩大曝光', completed: false, priority: 'medium' as const, startDate: '2025-12-12', endDate: '2025-12-15' }
        ],
        // W3 任务
        [
            { id: 'p1w3t1', content: '分析竞品价格，考虑是否调价', completed: false, priority: 'medium' as const, startDate: '2025-12-16', endDate: '2025-12-18' },
            { id: 'p1w3t2', content: '检查Review获取情况，跟进客户反馈', completed: false, priority: 'high' as const, startDate: '2025-12-17', endDate: '2025-12-19' },
            { id: 'p1w3t3', content: '继续优化广告投放策略', completed: false, priority: 'medium' as const, startDate: '2025-12-18', endDate: '2025-12-21' }
        ],
        // W4 任务
        [
            { id: 'p1w4t1', content: '总结首月数据，评估推广效果', completed: false, priority: 'high' as const, startDate: '2025-12-23', endDate: '2025-12-26' },
            { id: 'p1w4t2', content: '准备进入下阶段的关键词策略', completed: false, priority: 'high' as const, startDate: '2025-12-24', endDate: '2025-12-27' },
            { id: 'p1w4t3', content: '调整库存计划，根据销量预测补货', completed: false, priority: 'medium' as const, startDate: '2025-12-25', endDate: '2025-12-28' }
        ]
    ];

    const phase1: StrategyPhase = {
        id: '1',
        name: '新品推广期',
        startWeek: 1,
        endWeek: 4,
        strategyDescription: "1. 开启自动广告，积累关键词数据。\n2. 重点关注点击率(CTR)，优化主图和标题。\n3. 配合站外推广，快速提升销量排名。",
        flowDiagram: `graph LR
    A[新品上架] --> B[开启自动广告]
    B --> C[站外推广引流]
    C --> D[积累Review]
    D --> E[优化Listing]
    E --> F[提升BSR排名]`,
        weeks: Array.from({ length: 4 }, (_, i) => ({
            ...baseWeekStrategy,
            weekNumber: i + 1,
            basePrice: 35.99,
            dailyUnits: 10 + i * 2, // 10, 12, 14, 16
            enablePromotion: true,
            promotionDiscount: 50,
            promotionDailyUnits: 8,
            adSalesRatio: 80 - i * 5, // 80, 75, 70, 65
            cpc: Number((1.5 - i * 0.1).toFixed(2)), // 1.5, 1.4, 1.3, 1.2
            cvr: Number((5 + i * 1).toFixed(1)), // 5, 6, 7, 8
            tasks: phase1Tasks[i]
        }))
    };

    // 阶段2: 排名增长期 (W5-W8)
    const phase2Tasks = [
        // W5 任务
        [
            { id: 'p2w5t1', content: '下载搜索词报告，否定无效词', completed: false, priority: 'high' as const, startDate: '2025-12-30', endDate: '2026-01-02' },
            { id: 'p2w5t2', content: '开启手动精准广告，匹配核心词', completed: false, priority: 'high' as const, startDate: '2026-01-02', endDate: '2026-01-04' },
            { id: 'p2w5t3', content: '降低站外促销力度，测试自然流量', completed: false, priority: 'medium' as const, startDate: '2026-01-03', endDate: '2026-01-06' }
        ],
        // W6 任务
        [
            { id: 'p2w6t1', content: '测试提价 5-10%，观察转化率变化', completed: false, priority: 'high' as const, startDate: '2026-01-06', endDate: '2026-01-09' },
            { id: 'p2w6t2', content: '优化产品五点描述和标题', completed: false, priority: 'medium' as const, startDate: '2026-01-07', endDate: '2026-01-10' },
            { id: 'p2w6t3', content: '分析竞品广告策略，调整竞价', completed: false, priority: 'medium' as const, startDate: '2026-01-08', endDate: '2026-01-11' }
        ],
        // W7 任务
        [
            { id: 'p2w7t1', content: '检查广告ACOS，优化低效广告组', completed: false, priority: 'high' as const, startDate: '2026-01-13', endDate: '2026-01-16' },
            { id: 'p2w7t2', content: '扩展长尾关键词广告', completed: false, priority: 'medium' as const, startDate: '2026-01-14', endDate: '2026-01-17' },
            { id: 'p2w7t3', content: '跟进客户问题，维护好评率', completed: false, priority: 'medium' as const, startDate: '2026-01-15', endDate: '2026-01-18' }
        ],
        // W8 任务
        [
            { id: 'p2w8t1', content: '评估本阶段ROI，调整下阶段策略', completed: false, priority: 'high' as const, startDate: '2026-01-20', endDate: '2026-01-23' },
            { id: 'p2w8t2', content: '准备稳定期的Coupon和LD活动', completed: false, priority: 'medium' as const, startDate: '2026-01-21', endDate: '2026-01-24' },
            { id: 'p2w8t3', content: '检查库存健康度，避免超仓费', completed: false, priority: 'medium' as const, startDate: '2026-01-22', endDate: '2026-01-25' }
        ]
    ];

    const phase2: StrategyPhase = {
        id: '2',
        name: '排名增长期',
        startWeek: 5,
        endWeek: 8,
        strategyDescription: "1. 分析搜索词报告，否定无效流量。\n2. 开启手动精准广告，提升转化率。\n3. 适当提高售价，测试市场接受度。",
        flowDiagram: `graph LR
    A[分析数据] --> B[优化关键词]
    B --> C[提升转化率]
    C --> D[调整售价]
    D --> E[稳定排名]
    E --> F[准备盈利期]`,
        weeks: Array.from({ length: 4 }, (_, i) => ({
            ...baseWeekStrategy,
            weekNumber: i + 5,
            basePrice: 37.99,
            dailyUnits: 20 + i * 5, // 20, 25, 30, 35
            enableCoupon: true,
            couponDiscount: 20,
            adSalesRatio: 50 - i * 5, // 50, 45, 40, 35
            cpc: Number((1.2 - i * 0.05).toFixed(2)), // 1.2, 1.15, 1.1, 1.05
            cvr: Number((8 + i * 0.5).toFixed(1)), // 8, 8.5, 9, 9.5
            tasks: phase2Tasks[i]
        }))
    };

    // 阶段3: 稳定盈利期 (W9-W13)
    const phase3Tasks = [
        // W9 任务
        [
            { id: 'p3w9t1', content: '优化ACOS至<25%，降低广告占比', completed: false, priority: 'high' as const, startDate: '2026-01-27', endDate: '2026-01-30' },
            { id: 'p3w9t2', content: '监控库存周转率，调整补货节奏', completed: false, priority: 'high' as const, startDate: '2026-01-28', endDate: '2026-01-31' },
            { id: 'p3w9t3', content: '拓展长尾词广告组，增加流量入口', completed: false, priority: 'medium' as const, startDate: '2026-01-29', endDate: '2026-02-01' }
        ],
        // W10 任务
        [
            { id: 'p3w10t1', content: '检查产品毛利率，确保盈利健康', completed: false, priority: 'high' as const, startDate: '2026-02-03', endDate: '2026-02-06' },
            { id: 'p3w10t2', content: '优化产品Listing，提升自然排名', completed: false, priority: 'medium' as const, startDate: '2026-02-04', endDate: '2026-02-07' },
            { id: 'p3w10t3', content: '分析竞品动态，防止价格战', completed: false, priority: 'medium' as const, startDate: '2026-02-05', endDate: '2026-02-08' }
        ],
        // W11 任务
        [
            { id: 'p3w11t1', content: '评估是否需要开启LD提升销量', completed: false, priority: 'medium' as const, startDate: '2026-02-10', endDate: '2026-02-13' },
            { id: 'p3w11t2', content: '关注恶意Review，及时申诉处理', completed: false, priority: 'high' as const, startDate: '2026-02-11', endDate: '2026-02-14' },
            { id: 'p3w11t3', content: '计划Q4旺季备货和促销策略', completed: false, priority: 'medium' as const, startDate: '2026-02-12', endDate: '2026-02-15' }
        ],
        // W12 任务
        [
            { id: 'p3w12t1', content: '总结优化经验，复制到其他SKU', completed: false, priority: 'medium' as const, startDate: '2026-02-17', endDate: '2026-02-20' },
            { id: 'p3w12t2', content: '检查广告预算使用效率', completed: false, priority: 'medium' as const, startDate: '2026-02-18', endDate: '2026-02-21' },
            { id: 'p3w12t3', content: '维护客户满意度，降低退货率', completed: false, priority: 'high' as const, startDate: '2026-02-19', endDate: '2026-02-22' }
        ],
        // W13 任务
        [
            { id: 'p3w13t1', content: '完成90天复盘，总结成败经验', completed: false, priority: 'high' as const, startDate: '2026-02-24', endDate: '2026-02-27' },
            { id: 'p3w13t2', content: '制定下一个90天运营计划', completed: false, priority: 'high' as const, startDate: '2026-02-25', endDate: '2026-02-28' },
            { id: 'p3w13t3', content: '评估是否需要产品迭代或变体扩展', completed: false, priority: 'medium' as const, startDate: '2026-02-26', endDate: '2026-03-01' }
        ]
    ];

    const phase3: StrategyPhase = {
        id: '3',
        name: '稳定盈利期',
        startWeek: 9,
        endWeek: 13,
        strategyDescription: "1. 优化ACOS，降低广告占比。\n2. 关注库存周转，避免断货。\n3. 拓展长尾词，增加流量入口。",
        flowDiagram: `graph LR
    A[降低ACOS] --> B[优化库存]
    B --> C[提升毛利率]
    C --> D[维护排名]
    D --> E[计划扩展]
    E --> F[持续盈利]`,
        weeks: Array.from({ length: 5 }, (_, i) => ({
            ...baseWeekStrategy,
            weekNumber: i + 9,
            basePrice: 39.99,
            dailyUnits: 40 + i * 2, // 40, 42, 44, 46, 48
            enableCoupon: true,
            couponDiscount: 10,
            adSalesRatio: 30 - i * 2, // 30, 28, 26, 24, 22
            cpc: Number((1.0 - i * 0.05).toFixed(2)), // 1.0, 0.95, 0.9, 0.85, 0.8
            cvr: Number((10 + i * 0.5).toFixed(1)), // 10, 10.5, 11, 11.5, 12
            tasks: phase3Tasks[i]
        }))
    };

    return [phase1, phase2, phase3];
};
